import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Briefcase,
  Users,
  Package,
  Calendar,
  User,
  Clock,
  Edit2,
  CheckCircle,
  PauseCircle,
  Trash2,
  RotateCcw,
  ExternalLink,
  Send,
  RefreshCw
} from 'lucide-react'
import { Modal, Button } from '@components/common'
import { apiClient } from '@api'
import { formatDate } from '@utils'
import './ViewCompanyModal.css'

/**
 * ViewCompanyModal Component
 * Displays comprehensive company details in a modal
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {number} props.companyId - ID of company to view
 * @param {number} props.refreshTrigger - Trigger to refetch data (increments when data should refresh)
 * @param {Function} props.onEdit - Edit action handler
 * @param {Function} props.onActivate - Activate action handler
 * @param {Function} props.onSuspend - Suspend action handler
 * @param {Function} props.onArchive - Archive action handler
 * @param {Function} props.onRestore - Restore action handler
 * @param {Function} props.onSendActivation - Send activation email handler
 * @param {Function} props.onResendActivation - Resend activation email handler
 */
const ViewCompanyModal = ({
  isOpen,
  onClose,
  companyId,
  refreshTrigger = 0,
  onEdit,
  onActivate,
  onSuspend,
  onArchive,
  onRestore,
  onSendActivation,
  onResendActivation
}) => {
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (isOpen && companyId) {
      fetchCompanyDetails()
    }
  }, [isOpen, companyId])

  // Refetch when refreshTrigger changes (when actions are completed)
  useEffect(() => {
    if (isOpen && companyId && refreshTrigger > 0) {
      fetchCompanyDetails()
    }
  }, [refreshTrigger])

  const fetchCompanyDetails = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get(`/companies/${companyId}`)
      if (response.status === 200 && response.data) {
        setCompany(response.data)
      }
    } catch (err) {
      setError(err.message || 'Failed to load company details')
      console.error('Error fetching company details:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setCompany(null)
    setError(null)
    onClose()
  }

  const handleAction = (actionHandler) => {
    if (actionHandler && company) {
      // Close the modal first
      handleClose()
      // Then trigger the action (which will show confirmation dialog)
      actionHandler(company)
    }
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      active: 'status-badge-active',
      trial: 'status-badge-trial',
      pending: 'status-badge-pending',
      suspended: 'status-badge-suspended',
      cancelled: 'status-badge-cancelled'
    }
    return statusMap[status] || 'status-badge-pending'
  }

  const calculateDaysRemaining = () => {
    if (!company?.subscription_end) return null
    const endDate = new Date(company.subscription_end)
    const today = new Date()
    const diffTime = endDate - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  // Modal footer with action buttons
  const modalFooter = !loading && !error && company ? (
    <div className="view-company-modal-footer">
      <Button variant="secondary" onClick={handleClose}>
        Close
      </Button>

      <div className="view-company-quick-actions">
        {/* Activation Buttons - Show only for pending companies */}
        {company.status === 'pending' && !company.deleted_at && (
          <>
            {company.activation_token_sent_at ? (
              <Button
                variant="info"
                onClick={() => handleAction(onResendActivation)}
                size="small"
              >
                <RefreshCw size={16} />
                Resend Activation Email
              </Button>
            ) : (
              <Button
                variant="info"
                onClick={() => handleAction(onSendActivation)}
                size="small"
              >
                <Send size={16} />
                Send Activation Email
              </Button>
            )}
          </>
        )}

        {/* Edit Button - Hidden if cancelled or deleted */}
        {company.status !== 'cancelled' && !company.deleted_at && (
          <Button
            variant="primary"
            onClick={() => handleAction(onEdit)}
            size="small"
          >
            <Edit2 size={16} />
            Edit
          </Button>
        )}

        {/* Activate Button - Hidden if already active or deleted */}
        {company.status !== 'active' && !company.deleted_at && (
          <Button
            variant="success"
            onClick={() => handleAction(onActivate)}
            size="small"
          >
            <CheckCircle size={16} />
            Activate
          </Button>
        )}

        {/* Suspend Button - Hidden if already suspended/cancelled or deleted */}
        {company.status !== 'suspended' &&
          company.status !== 'cancelled' &&
          !company.deleted_at && (
            <Button
              variant="warning"
              onClick={() => handleAction(onSuspend)}
              size="small"
            >
              <PauseCircle size={16} />
              Suspend
            </Button>
          )}

        {/* Archive Button - Hidden if deleted */}
        {!company.deleted_at && (
          <Button
            variant="danger"
            onClick={() => handleAction(onArchive)}
            size="small"
          >
            <Trash2 size={16} />
            Archive
          </Button>
        )}

        {/* Restore Button - Only visible if deleted */}
        {company.deleted_at && (
          <Button
            variant="info"
            onClick={() => handleAction(onRestore)}
            size="small"
          >
            <RotateCcw size={16} />
            Restore
          </Button>
        )}
      </div>
    </div>
  ) : null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Company Details"
      size="large"
      footer={modalFooter}
      closeOnBackdrop={false}
    >
      {loading && (
        <div className="view-company-loading">
          <div className="view-company-spinner"></div>
          <p>Loading company details...</p>
        </div>
      )}

      {error && (
        <div className="view-company-error">
          <p className="view-company-error-message">{error}</p>
          <Button variant="primary" onClick={fetchCompanyDetails}>
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && company && (
        <div className="view-company-content">
          {/* Company Overview */}
          <section className="view-company-section">
            <h3 className="view-company-section-title">Company Overview</h3>
            <div className="view-company-overview">
              {company.logo ? (
                <img
                  src={company.logo}
                  alt={company.name}
                  className="view-company-logo"
                />
              ) : (
                <div className="view-company-logo-placeholder">
                  <Building2 size={32} />
                </div>
              )}
              <div className="view-company-overview-info">
                <h4 className="view-company-name">{company.name}</h4>
                <code className="view-company-slug">{company.slug}</code>
                <div className="view-company-badges">
                  <span className={`view-company-status-badge ${getStatusBadgeClass(company.status)}`}>
                    {company.status?.toUpperCase()}
                  </span>
                  {company.deleted_at && (
                    <span className="view-company-status-badge status-badge-archived">ARCHIVED</span>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section className="view-company-section">
            <h3 className="view-company-section-title">
              <Mail size={18} />
              Contact Information
            </h3>
            <div className="view-company-grid">
              <div className="view-company-field">
                <label className="view-company-label">
                  <Mail size={16} />
                  Email
                </label>
                <p className="view-company-value">
                  {company.email ? (
                    <a href={`mailto:${company.email}`} className="view-company-link">
                      {company.email}
                    </a>
                  ) : (
                    <span className="view-company-muted">Not provided</span>
                  )}
                </p>
              </div>

              <div className="view-company-field">
                <label className="view-company-label">
                  <Phone size={16} />
                  Phone
                </label>
                <p className="view-company-value">
                  {company.phone ? (
                    <a href={`tel:${company.phone}`} className="view-company-link">
                      {company.phone}
                    </a>
                  ) : (
                    <span className="view-company-muted">Not provided</span>
                  )}
                </p>
              </div>

              <div className="view-company-field view-company-field-full">
                <label className="view-company-label">
                  <Globe size={16} />
                  Website
                </label>
                <p className="view-company-value">
                  {company.website ? (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="view-company-link"
                    >
                      {company.website}
                      <ExternalLink size={14} className="view-company-external-icon" />
                    </a>
                  ) : (
                    <span className="view-company-muted">Not provided</span>
                  )}
                </p>
              </div>
            </div>
          </section>

          {/* Location Details */}
          <section className="view-company-section">
            <h3 className="view-company-section-title">
              <MapPin size={18} />
              Location Details
            </h3>
            <div className="view-company-grid">
              <div className="view-company-field view-company-field-full">
                <label className="view-company-label">Address</label>
                <p className="view-company-value">
                  {company.address || <span className="view-company-muted">Not provided</span>}
                </p>
              </div>

              <div className="view-company-field">
                <label className="view-company-label">City</label>
                <p className="view-company-value">
                  {company.city || <span className="view-company-muted">Not provided</span>}
                </p>
              </div>

              <div className="view-company-field">
                <label className="view-company-label">State/Province</label>
                <p className="view-company-value">
                  {company.state || <span className="view-company-muted">Not provided</span>}
                </p>
              </div>

              <div className="view-company-field">
                <label className="view-company-label">Country</label>
                <p className="view-company-value">
                  {company.country || <span className="view-company-muted">Not provided</span>}
                </p>
              </div>

              <div className="view-company-field">
                <label className="view-company-label">Postal Code</label>
                <p className="view-company-value">
                  {company.postal_code || <span className="view-company-muted">Not provided</span>}
                </p>
              </div>
            </div>
          </section>

          {/* Business Details */}
          <section className="view-company-section">
            <h3 className="view-company-section-title">
              <Briefcase size={18} />
              Business Details
            </h3>
            <div className="view-company-grid">
              <div className="view-company-field">
                <label className="view-company-label">Industry</label>
                <p className="view-company-value">
                  {company.industry || <span className="view-company-muted">Not provided</span>}
                </p>
              </div>

              <div className="view-company-field">
                <label className="view-company-label">
                  <Users size={16} />
                  Company Size
                </label>
                <p className="view-company-value">
                  {company.company_size || <span className="view-company-muted">Not provided</span>}
                </p>
              </div>
            </div>
          </section>

          {/* Package & Subscription */}
          <section className="view-company-section">
            <h3 className="view-company-section-title">
              <Package size={18} />
              Package & Subscription
            </h3>
            <div className="view-company-grid">
              <div className="view-company-field">
                <label className="view-company-label">Package</label>
                <p className="view-company-value">
                  {company.package ? (
                    <span className="view-company-package-badge">{company.package.title}</span>
                  ) : (
                    <span className="view-company-muted">No package assigned</span>
                  )}
                </p>
              </div>

              {company.package && (
                <div className="view-company-field">
                  <label className="view-company-label">Package Price</label>
                  <p className="view-company-value">
                    ${parseFloat(company.package.price).toFixed(2)}/month
                  </p>
                </div>
              )}

              <div className="view-company-field">
                <label className="view-company-label">
                  <Calendar size={16} />
                  Subscription Start
                </label>
                <p className="view-company-value">
                  {company.subscription_start ? (
                    formatDate(company.subscription_start)
                  ) : (
                    <span className="view-company-muted">Not started</span>
                  )}
                </p>
              </div>

              <div className="view-company-field">
                <label className="view-company-label">
                  <Calendar size={16} />
                  Subscription End
                </label>
                <p className="view-company-value">
                  {company.subscription_end ? (
                    <>
                      {formatDate(company.subscription_end)}
                      {calculateDaysRemaining() !== null && (
                        <span
                          className={`view-company-days-remaining ${
                            calculateDaysRemaining() < 0
                              ? 'expired'
                              : calculateDaysRemaining() <= 7
                                ? 'warning'
                                : 'active'
                          }`}
                        >
                          {calculateDaysRemaining() >= 0
                            ? `${calculateDaysRemaining()} days remaining`
                            : `Expired ${Math.abs(calculateDaysRemaining())} days ago`}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="view-company-muted">Not set</span>
                  )}
                </p>
              </div>
            </div>
          </section>

          {/* Activation Status - Show for pending companies with activation email sent */}
          {company.status === 'pending' && company.activation_token_sent_at && (
            <section className="view-company-section">
              <h3 className="view-company-section-title">
                <Mail size={18} />
                Activation Status
              </h3>
              <div className="view-company-activation-status">
                <div className="view-company-grid">
                  <div className="view-company-field">
                    <label className="view-company-label">Activation Email Sent</label>
                    <p className="view-company-value">
                      {formatDate(company.activation_token_sent_at)}
                    </p>
                  </div>

                  <div className="view-company-field">
                    <label className="view-company-label">Token Expires</label>
                    <p className="view-company-value">
                      {company.activation_token_expires_at ? (
                        <>
                          {formatDate(company.activation_token_expires_at)}
                          {new Date(company.activation_token_expires_at) < new Date() && (
                            <span className="view-company-expired-badge">Expired</span>
                          )}
                        </>
                      ) : (
                        <span className="view-company-muted">Not set</span>
                      )}
                    </p>
                  </div>

                  <div className="view-company-field">
                    <label className="view-company-label">Token Status</label>
                    <p className="view-company-value">
                      {company.activation_token_used ? (
                        <span className="view-company-used-badge">Used</span>
                      ) : (
                        <span className="view-company-pending-badge">Pending</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Compliance Manager */}
          <section className="view-company-section">
            <h3 className="view-company-section-title">
              <User size={18} />
              Compliance Manager
            </h3>
            <div className="view-company-grid">
              {company.compliance_manager ? (
                <>
                  <div className="view-company-field">
                    <label className="view-company-label">Name</label>
                    <p className="view-company-value">
                      {company.compliance_manager.first_name}{' '}
                      {company.compliance_manager.last_name}
                    </p>
                  </div>

                  <div className="view-company-field">
                    <label className="view-company-label">Email</label>
                    <p className="view-company-value">
                      <a
                        href={`mailto:${company.compliance_manager.email}`}
                        className="view-company-link"
                      >
                        {company.compliance_manager.email}
                      </a>
                    </p>
                  </div>

                  {company.compliance_manager.phone && (
                    <div className="view-company-field">
                      <label className="view-company-label">Phone</label>
                      <p className="view-company-value">
                        <a
                          href={`tel:${company.compliance_manager.phone}`}
                          className="view-company-link"
                        >
                          {company.compliance_manager.phone}
                        </a>
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="view-company-field view-company-field-full">
                  <p className="view-company-value view-company-muted">No compliance manager assigned</p>
                </div>
              )}
            </div>
          </section>

          {/* System Information */}
          <section className="view-company-section">
            <h3 className="view-company-section-title">
              <Clock size={18} />
              System Information
            </h3>
            <div className="view-company-grid">
              <div className="view-company-field">
                <label className="view-company-label">Created Date</label>
                <p className="view-company-value">{formatDate(company.created_at)}</p>
              </div>

              <div className="view-company-field">
                <label className="view-company-label">Last Updated</label>
                <p className="view-company-value">{formatDate(company.updated_at)}</p>
              </div>

              {company.deleted_at && (
                <div className="view-company-field">
                  <label className="view-company-label">Archived Date</label>
                  <p className="view-company-value view-company-danger">
                    {formatDate(company.deleted_at)}
                  </p>
                </div>
              )}

              <div className="view-company-field">
                <label className="view-company-label">Company ID</label>
                <p className="view-company-value">
                  <code>#{company.id}</code>
                </p>
              </div>
            </div>
          </section>
        </div>
      )}
    </Modal>
  )
}

ViewCompanyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  companyId: PropTypes.number,
  refreshTrigger: PropTypes.number,
  onEdit: PropTypes.func,
  onActivate: PropTypes.func,
  onSuspend: PropTypes.func,
  onArchive: PropTypes.func,
  onRestore: PropTypes.func,
  onSendActivation: PropTypes.func,
  onResendActivation: PropTypes.func
}

ViewCompanyModal.defaultProps = {
  companyId: null,
  refreshTrigger: 0,
  onEdit: null,
  onActivate: null,
  onSuspend: null,
  onArchive: null,
  onRestore: null,
  onSendActivation: null,
  onResendActivation: null
}

export default ViewCompanyModal
